// The tutorial's offer card (#1848 tickets 04, 18), for users who never open
// the menu: «Пройти обучение редактору?», or — while a unit is active and this
// is not the tutorial project — its resume form. The tutorial never starts by
// itself.
//
// One card, three surfaces: a Film Editor (`TutorialOfferCard`, mounted by the
// Editor), the projects list, and the screen the user lands on right after
// sign-up or login (`TutorialOfferHost`, mounted by the layouts). They never
// mount on the same screen, so only one card is ever up.
//
// Non-modal and never focused, like the tour's cards: its buttons keep the
// caret where it is. It sits where the end card does, above the bottom-right
// corner the support widget takes.

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useUserQuery } from '@/api/queries/useAuth';
import { tutorialActiveUnitAtom, tutorialOfferAnsweredAtom, tutorialPostAuthAtom, tutorialRunAtom } from '@/atoms/tutorial';
import { Spinner } from '@/components/ui/spinner';
import { paths } from '@/constants';
import { useUserTutorial } from '@/data/entities/userTutorial';
import { useProjectId } from '@/hooks';
import type { IProject, TutorialUnit } from '@/types';
import { TutorialEndCard } from './TutorialEndCard';
import { overlayAttr, TourButton } from './TutorialOverlay';
import { type OfferForm, type TutorialEvent, trackTutorial } from './tutorialAnalytics';
import { useTutorialEnabled } from './tutorialGate';
import {
    decideTutorialOffer,
    editorOffersTutorial,
    nextPostAuthMarker,
    type OfferPlace,
    type OfferSurface,
    offerSurface,
    postAuthHolds,
} from './tutorialOffer';
import { useOpenTutorialProject } from './useOpenTutorialProject';
import { useResumableUnit } from './useTutorialAccess';
import { useTutorialLifecycle } from './useTutorialLifecycle';

interface TutorialOfferCardProps {
    /** The Editor's project, `undefined` until it has loaded. */
    project: IProject | undefined;
    /** Whether the Editor renders Fountain. */
    rendersFountain: boolean;
}

/** The Editor's card. Behind the tutorial flag, the resume form too: with it
 *  off a stored active unit asks nothing. */
export function TutorialOfferCard({ project, rendersFountain }: TutorialOfferCardProps) {
    const enabled = useTutorialEnabled();
    const pathname = useScreenPath();
    return enabled ? <OfferCard place="editor" pathname={pathname} editorGate={editorOffersTutorial(project, rendersFountain)} /> : null;
}

/** The card off the Editor: on the projects list, and on any screen right
 *  after sign-up or login. In the Editor it leaves the card to the Editor's own. */
export function TutorialOfferHost() {
    const enabled = useTutorialEnabled();
    const pathname = useScreenPath();
    if (!enabled || pathname === paths.project.scene.editor) return null;
    return <OfferCard place={pathname === paths.company.projects ? 'projects' : 'other'} pathname={pathname} />;
}

/** The screen on show: the path without a trailing slash. */
function useScreenPath() {
    const { pathname } = useLocation();
    return pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
}

interface OfferCardProps {
    place: OfferPlace;
    pathname: string;
    /** The Editor's Film + Fountain gate, `undefined` while its project loads.
     *  Off the Editor there is no project to check: the tutorial runs in its own. */
    editorGate?: boolean;
}

function OfferCard({ place, pathname, editorGate }: OfferCardProps) {
    const [postAuth, setPostAuth] = useAtom(tutorialPostAuthAtom);
    const surface = offerSurface(place, postAuthHolds(postAuth, pathname));
    const editorProjectId = useProjectId();
    // Off the Editor no project is on screen, whatever the workspace remembers.
    const projectId = place === 'editor' ? editorProjectId : null;
    const run = useAtomValue(tutorialRunAtom);
    const { data: user } = useUserQuery();
    const { data: tutorial } = useUserTutorial({ userId: user?.id ?? 0 }, { enabled: user !== undefined });
    const activeUnit = useResumableUnit(tutorial);
    /** Answered here or on another surface: the card goes at once, before the write lands. */
    const [answered, setAnswered] = useAtom(tutorialOfferAnsweredAtom);
    /** The resume form's «Выйти» ended the run: its end card is up. */
    const [exited, setExited] = useState<{ unit: TutorialUnit; projectId: number } | null>(null);

    // The Editor's gate holds for its own surface only: right after login the
    // card shows in a TV Editor too.
    const gate = surface === 'editor' ? editorGate : true;
    // A run on screen here (steps or its end card) owns the corner.
    const runHere = projectId !== null && run?.projectId === projectId;
    // A stored unit waiting on the access is neither the offer nor the resume form yet.
    const settled = tutorial !== undefined && activeUnit !== undefined && gate !== undefined;
    const decision =
        surface === null || !settled || !gate || runHere
            ? null
            : decideTutorialOffer({ projectId, tutorial, activeUnit, answered, now: new Date() });
    const shown = decision !== null && decision.kind !== 'none';

    useEffect(() => {
        setPostAuth(marker => nextPostAuthMarker(marker, { kind: 'route', pathname }));
    }, [pathname, setPostAuth]);
    useEffect(() => {
        if (surface !== 'post-auth' || !settled) return;
        setPostAuth(marker => nextPostAuthMarker(marker, shown ? { kind: 'shown', pathname } : { kind: 'none' }));
    }, [surface, settled, shown, pathname, setPostAuth]);

    if (exited !== null) return <ExitedCard {...exited} onDone={() => setExited(null)} />;
    if (surface === null || decision === null || decision.kind === 'none') return null;

    /** Answered on this surface: answered on all of them. */
    const answer = () => {
        setAnswered(true);
        setPostAuth(marker => nextPostAuthMarker(marker, { kind: 'answered' }));
    };
    // Keyed by the surface: a card that passes from one surface to another
    // (the post-auth one to the projects list's) is a new showing.
    return decision.kind === 'offer' ? (
        <OfferForm key={surface} surface={surface} onAnswered={answer} />
    ) : (
        <ResumeForm
            key={surface}
            unit={decision.unit}
            tutorialProjectId={decision.projectId}
            surface={surface}
            onExited={exited => {
                answer();
                setExited(exited);
            }}
        />
    );
}

function Card({ testId, children }: { testId: string; children: ReactNode }) {
    return (
        <div
            {...overlayAttr}
            data-testid={testId}
            className="fixed right-6 bottom-24 z-[3500] flex w-[320px] flex-col gap-2 rounded-2xl bg-c24 p-4 text-sm text-text-secondary shadow-lg"
        >
            {children}
        </div>
    );
}

/** Sends `event` once per showing of the form — not again on its re-renders. */
function useTrackShown(event: TutorialEvent) {
    const sent = useRef(false);
    const eventRef = useRef(event);
    useEffect(() => {
        if (sent.current) return;
        sent.current = true;
        trackTutorial(eventRef.current);
    }, []);
}

function StartButton({
    label,
    unit = 'core',
    form,
    surface,
    open,
    isOpening,
    ready,
}: ReturnType<typeof useOpenTutorialProject> & { label: string; unit?: TutorialUnit; form: OfferForm; surface: OfferSurface }) {
    const start = () => {
        if (!ready || isOpening) return;
        trackTutorial({ name: 'offer_started', unit, form, surface });
        void open(unit);
    };
    return (
        <TourButton primary onClick={start}>
            {isOpening ? <Spinner size={14} /> : label}
        </TourButton>
    );
}

function OfferForm({ surface, onAnswered }: { surface: OfferSurface; onAnswered: () => void }) {
    const { t } = useTranslation();
    const opener = useOpenTutorialProject();
    const { recordRun, snoozeOffer } = useTutorialLifecycle();
    useTrackShown({ name: 'offer_shown', unit: 'core', form: 'offer', surface });

    const later = () => {
        onAnswered();
        trackTutorial({ name: 'offer_snoozed', unit: 'core', form: 'offer', surface });
        void snoozeOffer();
    };
    // «Не показывать» is the core skipped: no offer returns once `core` is set.
    const never = () => {
        onAnswered();
        trackTutorial({ name: 'offer_dismissed', unit: 'core', form: 'offer', surface });
        void recordRun('core', 'skipped');
    };

    return (
        <Card testId="tutorial-offer">
            <div className="flex items-baseline gap-2">
                <span className="font-semibold">{t('tutorial.offer.title')}</span>
                <span className="text-xs opacity-60">{t('tutorial.offer.duration')}</span>
            </div>
            <div className="leading-snug">{t('tutorial.offer.body')}</div>
            <div className="-mx-2 -mb-2 flex justify-between">
                <div className="flex">
                    <TourButton onClick={never}>{t('tutorial.offer.never')}</TourButton>
                    <TourButton onClick={later}>{t('tutorial.offer.later')}</TourButton>
                </div>
                <StartButton {...opener} form="offer" surface={surface} label={t('tutorial.offer.start')} />
            </div>
        </Card>
    );
}

interface ResumeFormProps {
    unit: TutorialUnit;
    tutorialProjectId: number;
    surface: OfferSurface;
    onExited: (exited: { unit: TutorialUnit; projectId: number }) => void;
}

function ResumeForm({ unit, tutorialProjectId, surface, onExited }: ResumeFormProps) {
    const { t } = useTranslation();
    // «Продолжить» is «Начать» again: the stored project reopens and the unit
    // restarts from its first step.
    const opener = useOpenTutorialProject();
    const { recordRun } = useTutorialLifecycle();
    const setActiveUnit = useSetAtom(tutorialActiveUnitAtom);
    const [run, setRun] = useAtom(tutorialRunAtom);
    useTrackShown({ name: 'offer_shown', unit, form: 'resume', surface });

    // «Выйти» before the unit is finished, as on the step card: the core is
    // skipped (a chapter stays not done), and the end card offers to delete
    // the project.
    const exit = () => {
        // No step on screen: the unit was left between runs.
        trackTutorial({ name: 'unit_exited', unit, step: null });
        setActiveUnit(null);
        if (run !== null && !run.ended) setRun(null);
        void recordRun(unit, 'skipped');
        onExited({ unit, projectId: tutorialProjectId });
    };

    return (
        <Card testId="tutorial-offer-resume">
            <div className="font-semibold">{t('tutorial.offer.resume-title')}</div>
            <div className="leading-snug">{t('tutorial.offer.resume-body')}</div>
            <div className="-mx-2 -mb-2 flex justify-between">
                <TourButton onClick={exit}>{t('tutorial.offer.exit')}</TourButton>
                <StartButton {...opener} form="resume" surface={surface} unit={unit} label={t('tutorial.offer.resume')} />
            </div>
        </Card>
    );
}

/** The end card after the resume form's «Выйти». The user stays in their own
 *  project: the delete needs no hop to the hub. */
function ExitedCard({ unit, projectId, onDone }: { unit: TutorialUnit; projectId: number; onDone: () => void }) {
    const { removeProject } = useTutorialLifecycle();
    return (
        <TutorialEndCard
            unit={unit}
            outcome="skipped"
            onClose={onDone}
            onDelete={() => {
                trackTutorial({ name: 'project_deleted', unit, outcome: 'skipped' });
                onDone();
                void removeProject(projectId, { leave: false });
            }}
        />
    );
}
